/*
 * Custom theme scripts.
 * Add storefront-specific JavaScript here instead of editing Horizon core assets.
 */

if (!customElements.get('leafony-ai-consult')) {
  customElements.define('leafony-ai-consult', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      const button = this.querySelector('[data-ai-copy]');
      const geminiLink = this.querySelector('[data-ai-gemini]');
      const prompt = this.querySelector('[data-ai-prompt]');
      const promptDetails = this.querySelector('[data-ai-prompt-details]');
      const status = this.querySelector('[data-ai-status]');
      if (!button || !prompt || !promptDetails || !status) return;
      this.dataset.ready = 'true';
      button.hidden = false;
      const copyPrompt = async (forGemini = false) => {
        status.textContent = '';
        try {
          await navigator.clipboard.writeText(prompt.value);
          status.textContent = forGemini ? status.dataset.geminiSuccess : status.dataset.success;
        } catch {
          promptDetails.open = true;
          prompt.focus();
          prompt.select();
          status.textContent = status.dataset.failure;
        }
      };
      button.addEventListener('click', () => copyPrompt());
      geminiLink?.addEventListener('click', () => copyPrompt(true));
    }
  });
}


/* Public-page Clarity: use Shopify's existing consent decision; never set consent for a visitor. */
(() => {
  if (window.leafonyClarityInstalled || location.origin !== 'https://shop.leafony.com') return;
  window.leafonyClarityInstalled = true;
  function clearCookies() {
    // A previous grant in _clck overrides the project's cookie-off default.
    let cleared = true;
    for (const name of ['_clck', '_clsk']) {
      for (const domain of ['', '; Domain=shop.leafony.com', '; Domain=leafony.com']) {
        try { document.cookie = `${name}=; Max-Age=0; Path=/${domain}; SameSite=Lax; Secure`; }
        catch { cleared = false; }
      }
    }
    try { return cleared && !document.cookie.split(';').some(value => /^(?:_clck|_clsk)=.+/.test(value.trim())); }
    catch { return false; }
  }
  // Available even on policy/account pages where the recorder has no config.
  document.addEventListener('leafony:analytics-refused', clearCookies);
  const restricted = () => navigator.globalPrivacyControl === true ||
    navigator.doNotTrack === '1' || window.doNotTrack === '1';
  if (restricted()) { clearCookies(); return; }
  const config = document.querySelector('meta[name="leafony-clarity-project"]');
  if (!config) return;
  const project = config.content;
  if (!/^[a-z0-9]+$/.test(project)) return;
  const safeUrl = (value, referrer = false) => {
    try {
      const url = new URL(value, location.href);
      if (!['https:', 'http:'].includes(url.protocol)) return false;
      if (referrer) return !url.search && !url.hash && !/\/(accounts?|customers|cart|checkouts?|orders?|search|password|gift_cards)(\/|$)|%40|@/i.test(url.pathname);
      if (url.origin !== 'https://shop.leafony.com') return false;
      if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:(?:products|collections|blogs)(?:\/|$)|$)/i.test(url.pathname)) return false;
      // Product variant URLs are public product metadata. All other query strings stay unrecorded.
      if ([...url.searchParams].some(([key, value]) => key !== 'variant' || !/^\d+$/.test(value))) return false;
      return !url.hash || Boolean(document.getElementById(decodeURIComponent(url.hash.slice(1))));
    } catch { return false; }
  };
  if (restricted() || !safeUrl(location.href) || (document.referrer && !safeUrl(document.referrer, true))) return;
  let active = false;
  let inserted = false;
  let storageState;
  let stopped = false;
  let script;
  const consent = () => window.Shopify?.customerPrivacy;
  const canRecord = () => {
    try {
      // Shopify combines the visitor's choice, region, and merchant privacy settings.
      const choice = consent()?.currentVisitorConsent().analytics;
      return !restricted() && consent()?.analyticsProcessingAllowed() === true &&
        (choice === 'yes' || choice === '');
    } catch { return false; }
  };
  function stop() {
    stopped = true;
    if (!active) return;
    // Protective stops must not revoke consent: consentv2 can schedule an SDK restart.
    window.clarity('stop');
    script?.remove();
    active = false;
  }
  function revoke() {
    if (!inserted) { clearCookies(); return; }
    window.clarity('consentv2', { analytics_Storage: 'denied', ad_Storage: 'denied' });
    stop();
    clearCookies();
    // Clarity may schedule a restart after denial; unload this document entirely.
    location.reload();
  }
  function syncConsent() {
    if (!canRecord()) { revoke(); return; }
    if (stopped) return;
    const storage = consent().currentVisitorConsent().analytics === 'yes' ? 'granted' : 'denied';
    if (storage === 'denied' && !clearCookies()) { stop(); return; }
    if (active) {
      // Dropping a previously granted cookie signal schedules an SDK restart.
      if (storageState === 'granted' && storage === 'denied') { revoke(); return; }
      storageState = storage;
      window.clarity('consentv2', { analytics_Storage: storage, ad_Storage: 'denied' });
      return;
    }
    if (window.clarity || document.querySelector('script[src*="clarity.ms/tag/"]')) return;
    document.documentElement.setAttribute('data-clarity-mask', 'true');
    window.clarity = function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
    window.clarity('consentv2', { analytics_Storage: storage, ad_Storage: 'denied' });
    storageState = storage;
    script = document.createElement('script');
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${project}`;
    active = true;
    inserted = true;
    document.head.appendChild(script);
  }
  // Capture before theme handlers open a cart/account dialog or accept free text.
  const privateControls = 'input, textarea, select, [contenteditable="true"], form, shopify-account, .account-button, cart-drawer-component, [data-testid="cart-drawer-trigger"], product-form-component, quick-add-component';
  for (const type of ['pointerdown', 'click', 'focusin', 'submit']) {
    document.addEventListener(type, event => {
      const path = event.composedPath?.() || [event.target];
      if (path.some(node => node.closest?.(privateControls))) { stop(); return; }
      const link = event.target.closest?.('a[href]');
      if (link && !safeUrl(link.href)) stop();
    }, true);
  }
  // Check a new URL before Shopify's variant/navigation code changes history.
  for (const method of ['pushState', 'replaceState']) {
    const original = history[method];
    history[method] = function (state, unused, url) {
      // Installed before the SDK: its outer wrapper checks active() after this returns.
      stop();
      if (inserted && url != null && !safeUrl(url)) {
        const next = new URL(url, location.href);
        if (next.origin === location.origin) {
          // Do not expose a private URL to a still-downloading SDK in this document.
          location[method === 'replaceState' ? 'replace' : 'assign'](next.href);
          return;
        }
      }
      return original.apply(this, arguments);
    };
  }
  window.addEventListener('popstate', stop, true);
  window.addEventListener('hashchange', () => { if (!safeUrl(location.href)) stop(); });
  window.addEventListener('pageshow', syncConsent);
  document.addEventListener('visitorConsentCollected', syncConsent);
  document.addEventListener('leafony:analytics-refused', stop);
  if (consent()) syncConsent();
  else window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], error => {
    if (!error && consent()) syncConsent();
  });
})();


/* Analytics preference entry for regions without an automatic Shopify banner. */
(() => {
  const template = document.getElementById('leafony-analytics-settings-template');
  const footer = document.querySelector('footer');
  if (!template || !footer || document.querySelector('[data-analytics-settings]')) return;
  footer.appendChild(template.content.cloneNode(true));
  // Resolve the current elements after theme/translation apps replace footer markup.
  const elements = () => {
    const ui = document.querySelector('[data-analytics-settings]');
    return ui ? {
      ui,
      open: ui.querySelector('[data-analytics-open]'),
      panel: ui.querySelector('section'),
      status: ui.querySelector('[data-analytics-status]'),
      choices: [...ui.querySelectorAll('[data-analytics-choice]')],
    } : null;
  };
  const api = () => window.Shopify?.customerPrivacy;
  const ready = () => typeof api()?.setTrackingConsent === 'function' && typeof api()?.currentVisitorConsent === 'function' && typeof api()?.analyticsProcessingAllowed === 'function';
  const restricted = () => navigator.globalPrivacyControl === true || navigator.doNotTrack === '1' || window.doNotTrack === '1';
  let saving = false;
  function render(message) {
    const current = elements();
    if (!current) return;
    const { choices, status } = current;
    choices.forEach(button => { button.disabled = saving || !ready() || (button.dataset.analyticsChoice === 'allow' && restricted()); });
    try {
      const value = ready() ? api().currentVisitorConsent().analytics : '';
      const allowed = ready() && api().analyticsProcessingAllowed() === true && !restricted();
      const state = value === 'no' || restricted() ? 'denied' :
        !allowed ? 'unknown' : value === 'yes' ? 'granted' : value === '' ? 'cookieless' : 'unknown';
      status.textContent = message || status.dataset[state];
      if (restricted()) status.textContent += ' ' + status.dataset.restricted;
    } catch {
      choices.forEach(button => { button.disabled = true; });
      status.textContent = status.dataset.error;
    }
  }
  function show() {
    const current = elements();
    if (!current) return;
    const { panel, open, status } = current;
    panel.hidden = false;
    open.setAttribute('aria-expanded', 'true');
    render(ready() ? null : status.dataset.loading);
    if (ready()) return;
    if (typeof window.Shopify?.loadFeatures !== 'function') { render(status.dataset.error); return; }
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
      render(error || !ready() ? elements()?.status.dataset.error : null);
    });
  }
  function close() {
    const current = elements();
    if (!current) return;
    current.panel.hidden = true;
    current.open.setAttribute('aria-expanded', 'false');
    current.open.focus();
  }
  function choose(button) {
    if (!ready() || saving || (button.dataset.analyticsChoice === 'allow' && restricted())) return;
    const allow = button.dataset.analyticsChoice === 'allow';
    if (!allow) document.dispatchEvent(new Event('leafony:analytics-refused'));
    const { status } = elements();
    const failure = allow ? status.dataset.error : status.dataset.errorDenied;
    saving = true;
    render(status.dataset.loading);
    try {
      // Granular update: preserve marketing, preferences, and sale_of_data exactly.
      api().setTrackingConsent({ analytics: allow }, () => {
        saving = false;
        try {
          const saved = api().currentVisitorConsent().analytics === (allow ? 'yes' : 'no');
          render(saved ? null : failure);
        } catch { render(failure); }
      });
    } catch { saving = false; render(failure); }
  }
  document.addEventListener('click', event => {
    if (!event.target.closest?.('[data-analytics-settings]')) return;
    if (event.target.closest('[data-analytics-open]')) show();
    else if (event.target.closest('[data-analytics-close]')) close();
    else {
      const button = event.target.closest('[data-analytics-choice]');
      if (button) choose(button);
    }
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && event.target.closest?.('[data-analytics-settings]')) close();
  });
  document.addEventListener('visitorConsentCollected', () => { if (elements()?.panel.hidden === false) render(); });
})();

(() => {
  const requests = new Map();
  const products = new Map([
    ['6714333790383', ['Basic Kit 2', 'basic-kit-2']],
    ['8263517831343', ['AC07 LoRa Mary', 'ac07-lora-mary']],
    ['8263511998639', ['AC08 LTE-M Mary', 'ac08-lte-m-mary']],
    ['8263518290095', ['AV06 1.8V–5.5V', 'av06-1-8v-5-5v']],
    ['8624518234287', ['AZ01C USB-C', 'az01c-usb-cリーフ']],
    ['8607841517743', ['AX01 Shield', 'ax01-shield']],
    ['8644949278895', ['AX04 Spacer', 'ax04-spacer']],
    ['8608881901743', ['AX05B I Meas', 'ax05b-current-measurement']],
  ]);
  const safeUrl = (raw, origins, prefix = '/') => {
    try {
      const url = new URL(raw);
      return url.protocol === 'https:' && origins.includes(url.origin) && url.pathname.startsWith(prefix) && !url.username && !url.password ? url.href : null;
    } catch { return null; }
  };
  const feedUrl = raw => {
    const url = new URL(raw);
    const local = ['localhost', '127.0.0.1'].includes(location.hostname) && ['localhost', '127.0.0.1'].includes(url.hostname) && url.protocol === 'http:';
    if ((!local && url.origin !== 'https://mitdir.jp') || url.pathname !== '/feeds/leafony.json' || url.search || url.hash || url.username || url.password) throw new Error('Invalid feed');
    return url;
  };
  const readJson = url => {
    if (!requests.has(url.href)) requests.set(url.href, fetch(url.href, { credentials: 'omit', referrerPolicy: 'no-referrer', signal: AbortSignal.timeout(8000) }).then(response => {
      if (!response.ok) throw new Error('Content unavailable');
      return response.json();
    }).catch(error => { requests.delete(url.href); throw error; }));
    return requests.get(url.href);
  };
  const readArticles = async url => {
    const feed = await readJson(url);
    if (feed.version !== 1 || !Array.isArray(feed.articles)) throw new Error('Invalid feed');
    const seen = new Set();
    return feed.articles.filter(article => {
      if (!article || typeof article.id !== 'string' || !article.id || seen.has(article.id) || typeof article.title !== 'string' || typeof article.summary !== 'string' || article.url !== `https://mitdir.jp/column/${encodeURIComponent(article.id)}`) return false;
      seen.add(article.id);
      return true;
    });
  };
  const readerUrl = (element, id) => {
    const url = new URL(element.dataset.readerUrl, location.origin);
    if (url.origin !== location.origin || !url.pathname.endsWith('/blogs/columns') || url.searchParams.get('view') !== 'leafony-columns') throw new Error('Invalid reader');
    if (id) url.searchParams.set('article', id);
    else url.searchParams.delete('article');
    return url;
  };
  const dateElement = (raw, locale, prefix = '') => {
    const date = new Date(raw);
    if (!Number.isFinite(date.getTime())) return null;
    const time = document.createElement('time');
    time.dateTime = date.toISOString();
    time.textContent = prefix + new Intl.DateTimeFormat(locale || 'ja', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
    return time;
  };
  const articleImage = (value, hero = false) => {
    const src = value && safeUrl(value.url, ['https://cms.mitdir.jp', 'https://mitdir.jp']);
    if (!src) return null;
    const image = document.createElement('img');
    image.src = src;
    image.alt = '';
    image.loading = hero ? 'eager' : 'lazy';
    image.decoding = 'async';
    if (hero) image.fetchPriority = 'high';
    image.width = Number.isInteger(value.width) && value.width > 0 ? value.width : 1200;
    image.height = Number.isInteger(value.height) && value.height > 0 ? value.height : 630;
    return image;
  };
  const articleCard = (article, element) => {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = readerUrl(element, article.id).href;
    link.className = 'custom-columns__article';
    const image = articleImage(article.image);
    if (image) link.append(image);
    const time = dateElement(article.published_at, element.dataset.locale);
    if (time) link.append(time);
    const title = document.createElement('h3');
    title.textContent = article.title;
    const summary = document.createElement('p');
    summary.textContent = article.summary;
    link.append(title, summary);
    item.append(link);
    return item;
  };
  const bodyFragment = (html, article, articles, element) => {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const allowed = new Set('p div section article h1 h2 h3 h4 h5 h6 ul ol li strong em b i u s small span blockquote pre code br hr a img figure figcaption table caption thead tbody tfoot tr th td dl dt dd sup sub details summary'.split(' '));
    const blocked = new Set('script style iframe object embed svg math form button input select textarea meta link base audio video source'.split(' '));
    const known = new Map(articles.map(value => [value.id, value]));
    const safeId = value => typeof value === 'string' && /^leafony-content-[A-Za-z][A-Za-z0-9_.:-]{0,127}$/.test(value) ? value : null;
    const linkUrl = raw => {
      try {
        if (typeof raw !== 'string' || !raw.trim()) return null;
        const url = new URL(raw, article.url);
        if (!['https:', 'mailto:', 'tel:'].includes(url.protocol) || url.username || url.password) return null;
        if (url.origin === 'https://mitdir.jp') {
          const match = url.pathname.match(/^\/column\/([^/]+)\/?$/);
          const id = match && decodeURIComponent(match[1]).split('--').pop();
          if (id && known.has(id)) {
            const local = readerUrl(element, id);
            if (url.hash) {
              const rawId = decodeURIComponent(url.hash.slice(1));
              const idValue = safeId(rawId.startsWith('leafony-content-') ? rawId : `leafony-content-${rawId}`);
              if (idValue) local.hash = idValue;
            }
            return local.href;
          }
          if (url.pathname.replace(/\/$/, '') === '/column' && url.searchParams.get('tag') === 'Leafony') return readerUrl(element).href;
        }
        return url.href;
      } catch { return null; }
    };
    const append = (node, parent) => {
      if (node.nodeType === Node.TEXT_NODE) { parent.append(document.createTextNode(node.textContent)); return; }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const tag = node.tagName.toLowerCase();
      if (blocked.has(tag)) return;
      if (!allowed.has(tag)) { for (const child of node.childNodes) append(child, parent); return; }
      const next = document.createElement(tag === 'h1' ? 'h2' : tag);
      const id = safeId(node.getAttribute('id'));
      if (id) next.id = id;
      if (tag === 'a') {
        const href = linkUrl(node.getAttribute('href'));
        if (href) { next.href = href; next.rel = 'noopener noreferrer'; }
        if (node.getAttribute('target') === '_blank') next.target = '_blank';
      }
      if (tag === 'img') {
        const src = safeUrl(node.getAttribute('src'), ['https://cms.mitdir.jp', 'https://mitdir.jp']);
        if (!src) return;
        next.src = src;
        next.alt = node.getAttribute('alt') || '';
        next.loading = 'lazy';
        next.decoding = 'async';
        const width = Number(node.getAttribute('width'));
        const height = Number(node.getAttribute('height'));
        if (Number.isInteger(width) && width > 0 && width <= 8000) next.width = width;
        if (Number.isInteger(height) && height > 0 && height <= 8000) next.height = height;
      }
      for (const name of ['colspan', 'rowspan']) {
        const value = Number(node.getAttribute(name));
        if (['th', 'td'].includes(tag) && Number.isInteger(value) && value > 0 && value <= 30) next.setAttribute(name, String(value));
      }
      if (tag === 'th' && ['row', 'col', 'rowgroup', 'colgroup'].includes(node.getAttribute('scope'))) next.setAttribute('scope', node.getAttribute('scope'));
      if (tag === 'ol' && /^-?\d{1,4}$/.test(node.getAttribute('start') || '')) next.start = Number(node.getAttribute('start'));
      if (tag === 'pre') { next.tabIndex = 0; next.setAttribute('aria-label', element.dataset.codeText); }
      for (const child of node.childNodes) append(child, next);
      if (tag === 'table') {
        const wrapper = document.createElement('div');
        wrapper.className = 'custom-column-reader__table';
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', element.dataset.tableText);
        wrapper.append(next);
        parent.append(wrapper);
      } else parent.append(next);
    };
    const fragment = document.createDocumentFragment();
    for (const child of parsed.body.childNodes) append(child, fragment);
    return fragment;
  };
  if (!customElements.get('leafony-columns')) customElements.define('leafony-columns', class extends HTMLElement {
    async connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      const list = this.querySelector('[data-column-list]');
      if (!list) return;
      try {
        const all = await readArticles(feedUrl(this.dataset.feedUrl));
        const productId = this.dataset.productId;
        const articles = all.filter(article => !productId || (Array.isArray(article.product_ids) && article.product_ids.includes(productId))).slice(0, 3);
        const fragment = document.createDocumentFragment();
        for (const article of articles) fragment.append(articleCard(article, this));
        if (fragment.childElementCount) {
          list.replaceChildren(fragment);
          this.hidden = false;
        }
      } catch { }
    }
  });
  if (!customElements.get('leafony-column-reader')) customElements.define('leafony-column-reader', class extends HTMLElement {
    async connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      const status = this.querySelector('[data-reader-status]');
      const retry = this.querySelector('[data-reader-retry]');
      const heading = this.querySelector('[data-reader-heading]');
      const intro = this.querySelector('[data-reader-intro]');
      const list = this.querySelector('[data-reader-list]');
      const content = this.querySelector('[data-reader-content]');
      if (!status || !retry || !heading || !intro || !list || !content) return;
      retry.addEventListener('click', () => location.reload());
      this.setAttribute('aria-busy', 'true');
      try {
        const url = feedUrl(this.dataset.feedUrl);
        const articles = await readArticles(url);
        const parameters = new URL(location.href).searchParams;
        if (!parameters.has('article')) {
          this.dataset.view = 'list';
          const fragment = document.createDocumentFragment();
          for (const article of articles) fragment.append(articleCard(article, this));
          list.replaceChildren(fragment);
          list.hidden = !articles.length;
          status.hidden = articles.length > 0;
          if (!articles.length) status.textContent = this.dataset.emptyText;
          return;
        }
        this.dataset.view = 'article';
        const id = parameters.get('article');
        const selected = parameters.getAll('article').length === 1 && articles.find(article => article.id === id);
        if (!selected) { status.textContent = this.dataset.unknownText; return; }
        heading.textContent = selected.title;
        intro.hidden = true;
        const expected = `https://mitdir.jp/feeds/leafony/${encodeURIComponent(id)}.json`;
        if (selected.content_url !== expected) throw new Error('Invalid article source');
        const detailUrl = new URL(expected);
        detailUrl.host = url.host;
        detailUrl.protocol = url.protocol;
        const detail = await readJson(detailUrl);
        const article = detail.article;
        if (detail.version !== 1 || !article || article.id !== id || article.url !== selected.url || typeof article.title !== 'string' || typeof article.content_html !== 'string' || !article.content_html.trim() || article.content_html.length > 1500000) throw new Error('Invalid article');
        const fragment = document.createDocumentFragment();
        const metadata = document.createElement('div');
        metadata.className = 'custom-column-reader__dates';
        const published = dateElement(article.published_at, this.dataset.locale);
        const updated = article.updated_at !== article.published_at && dateElement(article.updated_at, this.dataset.locale, this.dataset.updatedText + ' ');
        if (published) metadata.append(published);
        if (updated) metadata.append(updated);
        fragment.append(metadata);
        const image = articleImage(article.image, true);
        if (image) { image.className = 'custom-column-reader__hero'; fragment.append(image); }
        const body = document.createElement('div');
        body.className = 'custom-column-reader__body';
        body.append(bodyFragment(article.content_html, article, articles, this));
        fragment.append(body);
        const source = document.createElement('p');
        source.className = 'custom-column-reader__source';
        const sourceLink = document.createElement('a');
        sourceLink.href = article.url;
        sourceLink.textContent = this.dataset.sourceText;
        source.append(sourceLink);
        fragment.append(source);
        const related = Array.isArray(article.product_ids) ? [...new Set(article.product_ids)].filter(value => products.has(value)) : [];
        if (related.length) {
          const section = document.createElement('section');
          section.className = 'custom-column-reader__products';
          const title = document.createElement('h2');
          title.textContent = this.dataset.relatedText;
          const note = document.createElement('p');
          note.textContent = this.dataset.requirementsText;
          const links = document.createElement('ul');
          const shopRoot = readerUrl(this).pathname.replace(/blogs\/columns$/, '');
          for (const id of related) {
            const [name, handle] = products.get(id);
            const item = document.createElement('li');
            const link = document.createElement('a');
            link.href = `${shopRoot}products/${encodeURIComponent(handle)}`;
            link.textContent = name;
            item.append(link);
            links.append(item);
          }
          section.append(title, note, links);
          fragment.append(section);
        }
        content.replaceChildren(fragment);
        heading.textContent = article.title;
        document.title = `${article.title} | Leafony Shop`;
        content.hidden = false;
        status.hidden = true;
        if (location.hash) {
          const target = document.getElementById(location.hash.slice(1));
          if (target && content.contains(target)) target.scrollIntoView();
        }
      } catch {
        status.hidden = false;
        status.textContent = this.dataset.errorText;
        retry.hidden = false;
      } finally { this.setAttribute('aria-busy', 'false'); }
    }
  });
})();

(() => {
  const root = document.getElementById("leafony-quote");
  if (!root) return;
  const strings = JSON.parse(
    document.getElementById("quote-strings").textContent,
  );
  const status = document.getElementById("quote-status"),
    error = document.getElementById("quote-error"),
    form = document.getElementById("quote-form"),
    button = document.getElementById("quote-issue");
  let base;
  try {
    base = new URL(root.dataset.apiBase);
    const local = ["localhost", "127.0.0.1"].includes(location.hostname) && ["localhost", "127.0.0.1"].includes(base.hostname);
    if ((base.protocol !== "https:" && !(local && base.protocol === "http:")) || base.username || base.password || base.pathname !== "/" || base.search || base.hash) throw new Error();
  } catch {
    status.textContent = strings.unavailable;
    return;
  }
  const
    money = (value) =>
      new Intl.NumberFormat("ja-JP", {
        style: "currency",
        currency: "JPY",
        maximumFractionDigits: 0,
      }).format(value);
  let items = [],
    preview = null,
    inFlight = false;
  const hash = async (value) =>
    [
      ...new Uint8Array(
        await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value)),
      ),
    ]
      .map((n) => n.toString(16).padStart(2, "0"))
      .join("");
  async function api(path, body) {
    const res = await fetch(new URL(path, base), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "omit",
      signal: AbortSignal.timeout(20000),
    });
    const data = await res.json();
    if (!res.ok) {
      const e = new Error(data.message || strings.unavailable);
      e.status = res.status;
      throw e;
    }
    return data;
  }
  function render(p) {
    const body = document.getElementById("quote-lines");
    body.replaceChildren();
    for (const line of p.lines) {
      const row = document.createElement("tr"),
        name = document.createElement("td");
      name.textContent = line.title;
      if (line.variant) {
        const small = document.createElement("small");
        small.textContent = line.variant;
        name.append(small);
      }
      const quantity = document.createElement("td");
      quantity.textContent = String(line.quantity);
      const total = document.createElement("td");
      total.textContent = money(line.total);
      row.append(name, quantity, total);
      body.append(row);
    }
    for (const field of ["subtotal", "shipping", "tax", "total"])
      document.getElementById("quote-" + field).textContent = money(p[field]);
  }
  async function refresh() {
    preview = await api("/api/preview", { country: "JP", items });
    render(preview.pricing);
    document.getElementById("quote-content").hidden = false;
    status.textContent = strings.ready;
    button.disabled = false;
  }
  async function load() {
    try {
      const route = window.Shopify?.routes?.root || "/";
      const res = await fetch(route + "cart.js", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(strings.unavailable);
      const cart = await res.json();
      if (!cart.items?.length) {
        status.textContent = strings.empty;
        return;
      }
      if (
        cart.currency !== "JPY" ||
        root.dataset.taxesIncluded !== "true" ||
        cart.items.some(
          (i) =>
            i.selling_plan_allocation ||
            Object.values(i.properties || {}).some(
              (v) => v !== "" && v !== null,
            ),
        )
      )
        throw new Error(strings.unsupported);
      items = cart.items.map((i) => ({
        variantId:
          "gid://shopify/ProductVariant/" + String(i.variant_id || i.id),
        quantity: i.quantity,
      }));
      await refresh();
    } catch (e) {
      status.textContent = e.message || strings.unavailable;
    }
  }
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (inFlight || !preview || !form.reportValidity()) return;
    inFlight = true;
    button.disabled = true;
    button.textContent = strings.issuing;
    error.textContent = "";
    try {
      const data = new FormData(form),
        recipient = {
          company: String(data.get("company")).trim(),
          department: String(data.get("department") || "").trim(),
          contact: String(data.get("contact") || "").trim(),
        };
      const fingerprint = await hash(
        JSON.stringify({
          items,
          recipient,
          price: preview.hash,
          day: preview.quoteDay,
        }),
      );
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem("leafony.quote.request"));
      } catch {}
      const requestId =
        saved?.fingerprint === fingerprint
          ? saved.requestId
          : [...crypto.getRandomValues(new Uint8Array(32))]
              .map((b) => b.toString(16).padStart(2, "0"))
              .join("");
      try {
        sessionStorage.setItem(
          "leafony.quote.request",
          JSON.stringify({ fingerprint, requestId }),
        );
      } catch {}
      const result = await api("/api/quotes", {
        country: "JP",
        items,
        recipient,
        previewHash: preview.hash,
        requestId,
      });
      const url = new URL(result.url);
      if (
        url.origin !== base.origin ||
        !/^\/q\/[a-f0-9]{64}$/.test(url.pathname)
      )
        throw new Error(strings.unavailable);
      window.location.assign(url.href);
    } catch (e) {
      error.textContent = e.message || strings.unavailable;
      if (e.status === 409) {
        try {
          await refresh();
        } catch {
          preview = null;
        }
      }
      inFlight = false;
      button.disabled = !preview;
      button.textContent = strings.issue;
    }
  });
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      inFlight = false;
      button.disabled = !preview;
      button.textContent = strings.issue;
    }
  });
  load();
})();
